import type { ShortLocaleEnum } from 'schema/locale';
import { Environment, EnvironmentKeys, environment } from '../../constants/environment';
import { StoreCode, storeCodeSchema } from '../../schema/storeCode';

export enum Feature {
  paymentMethodDefault,
  storeQA,
  metalScoop,
  shaker,
  canister,
  referFriends,
  V53,
  SwitchV53Product,
  shippingMethodUpdate,
  ShopV3,
  AgzQuickBuy,
  apacCurrencyMigration
}

type FeatureFlag = {
  [key in Feature]: {
    environment: Environment;
    store?: StoreCode[];
    language?: ShortLocaleEnum[];
    exceptions?: Partial<Record<StoreCode, EnvironmentKeys[]>>;
  };
};

const featureFlags: FeatureFlag = {
  [Feature.paymentMethodDefault]: {
    environment: Environment.none
  },
  [Feature.storeQA]: {
    environment: Environment.stage
  },
  [Feature.metalScoop]: {
    environment: Environment.production || Environment.stage,
    store: [
      storeCodeSchema.enum.us,
      storeCodeSchema.enum.qa,
      storeCodeSchema.enum.eu,
      storeCodeSchema.enum.uk
    ]
  },
  [Feature.shaker]: {
    environment: Environment.production,
    store: [storeCodeSchema.enum.us, storeCodeSchema.enum.qa]
  },
  [Feature.canister]: {
    environment: Environment.production,
    store: [storeCodeSchema.enum.us, storeCodeSchema.enum.qa]
  },
  [Feature.referFriends]: {
    environment: Environment.production || Environment.stage,
    store: [
      storeCodeSchema.enum.eu,
      storeCodeSchema.enum.uk,
      storeCodeSchema.enum.us,
      storeCodeSchema.enum.qa,
      storeCodeSchema.enum.dev,
      storeCodeSchema.enum.ukd,
      storeCodeSchema.enum.eud
    ]
  },
  [Feature.V53]: {
    environment: Environment.production || Environment.stage,
    store: [
      storeCodeSchema.enum.us,
      storeCodeSchema.enum.qa,
      storeCodeSchema.enum.dev
      // storeCodeSchema.enum.eu,
      // storeCodeSchema.enum.uk,
      // storeCodeSchema.enum.ukd,
      // storeCodeSchema.enum.eud,
    ]
  },
  [Feature.SwitchV53Product]: {
    environment: Environment.stage,
    store: [
      storeCodeSchema.enum.us,
      storeCodeSchema.enum.qa,
      storeCodeSchema.enum.dev,
      storeCodeSchema.enum.ukd,
      storeCodeSchema.enum.eud
    ]
  },
  [Feature.shippingMethodUpdate]: {
    environment: Environment.stage,
    store: [
      storeCodeSchema.enum.eu,
      storeCodeSchema.enum.uk,
      storeCodeSchema.enum.us,
      storeCodeSchema.enum.qa,
      storeCodeSchema.enum.dev,
      storeCodeSchema.enum.ukd,
      storeCodeSchema.enum.eud
    ]
  },
  [Feature.ShopV3]: {
    environment: Environment.stage
  },
  [Feature.AgzQuickBuy]: {
    environment: Environment.stage
  },
  [Feature.apacCurrencyMigration]: {
    environment: Environment.production || Environment.stage
  }
};

export function isEnabled(
  feature: Feature,
  store?: StoreCode,
  language?: ShortLocaleEnum
): boolean {
  const {
    environment: featureEnv,
    store: featureStore,
    language: featureLanguage,
    exceptions: featureExceptions
  } = featureFlags[feature];

  let returnValue = false;

  if (featureEnv === undefined) {
    return false;
  }

  switch (environment) {
    case 'none':
      returnValue = featureEnv >= Environment.none;
      break;
    case 'local':
      returnValue = featureEnv >= Environment.local;
      break;
    case 'test':
      returnValue = featureEnv >= Environment.test;
      break;
    case 'development':
      returnValue = featureEnv >= Environment.development;
      break;
    case 'stage':
      returnValue = featureEnv >= Environment.stage;
      break;
    case 'production':
      returnValue = featureEnv >= Environment.production;
      break;
    default:
      break;
  }

  if (featureStore) {
    returnValue = returnValue && store ? featureStore.includes(store) : false;
  }

  if (featureLanguage) {
    returnValue = returnValue && language ? featureLanguage.includes(language) : false;
  }

  if (featureExceptions) {
    Object.keys(featureExceptions).forEach((exceptionStore: StoreCode) => {
      if (store === exceptionStore && featureExceptions[exceptionStore]?.includes(environment)) {
        returnValue = false;
      }
    });
  }

  return returnValue;
}
